/**
 * Messages queries (TanStack Query).
 *
 * - useMessages       → sayfalı liste (initial load)
 * - useSendMessage    → mesaj gönderme mutation
 * - useMessageStream  → SSE EventSource subscription, yeni mesajları cache'e ekler
 * - useUnreadCount    → unread message count
 */

import { useEffect, useRef } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'

import { api } from '@/lib/api'
import { SSE_RECONNECT_MS } from '@shared/constants/limits'

export type MessageRow = {
  id: string
  purchaseListId: string
  senderUserId: string
  senderCompanyId: string
  body: string
  createdAt: string
  deletedAt: string | null
  senderCompany: { id: string; name: string }
  senderUser: { id: string; fullName: string; role: string }
  attachments: Array<{
    id: string
    messageId: string
    url: string
    fileName: string
    fileSize: number
    mimeType: string
  }>
}

export type SendMessageBody = {
  body: string
  attachmentIds?: string[]
}

export type UploadedChatAttachment = {
  uploadId: string
  url: string
  fileName: string
  fileSize: number
  mimeType: string
}

type MessagePage = {
  items: MessageRow[]
  nextCursor: string | null
}

export const messageKeys = {
  byList: (listId: string) => ['messages', 'list', listId] as const,
  rooms: ['messages', 'rooms'] as const,
}

export function useMessages(listId: string | undefined) {
  return useInfiniteQuery({
    queryKey: listId ? messageKeys.byList(listId) : ['noop'],
    queryFn: async ({ pageParam }) => {
      const search = new URLSearchParams({ pageSize: '50' })
      if (pageParam) search.set('before', pageParam)
      const res = await api<{ ok: true; data: MessagePage }>(
        `/purchase-lists/${listId}/messages?${search.toString()}`,
      )
      return res.data
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: Boolean(listId),
    select: (data) => {
      const seen = new Set<string>()
      return data.pages
        .slice()
        .reverse()
        .flatMap((page) => page.items)
        .filter((message) => {
          if (seen.has(message.id)) return false
          seen.add(message.id)
          return true
        })
    },
    // SSE çalışmazsa fallback: 5 saniyede bir polling
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  })
}

export function useSendMessage(listId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: SendMessageBody) => {
      const res = await api<{ ok: true; data: MessageRow }>(`/purchase-lists/${listId}/messages`, {
        method: 'POST',
        body,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageKeys.byList(listId) })
      qc.invalidateQueries({ queryKey: messageKeys.rooms })
    },
  })
}

export function useUploadChatAttachment() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await api<{
        ok: true
        data: UploadedChatAttachment
      }>('/uploads/chat', {
        method: 'POST',
        body: formData,
      })
      return response.data
    },
  })
}

export async function deletePendingChatAttachment(uploadId: string): Promise<void> {
  await api(`/uploads/chat/${uploadId}`, { method: 'DELETE' })
}

export function useMarkRoomRead(listId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api(`/purchase-lists/${listId}/messages/read`, { method: 'POST' })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageKeys.rooms })
    },
  })
}

export type ChatRoom = {
  listId: string
  listTitle: string
  listStatus: 'OPEN' | 'AWARDED' | 'EXPIRED' | 'CANCELLED'
  otherCompanyId: string
  otherCompanyName: string
  buyerCompany: { id: string; name: string }
  supplierCompany: { id: string; name: string } | null
  lastMessage: {
    body: string
    senderCompanyId: string
    createdAt: string
  } | null
  lastMessageAt: string | null
  unreadCount: number
  totalMessages: number
}

/**
 * Sidebar Mesajlar linki için sohbet odaları listesi.
 *
 *  - Buyer: kendi mesajlaşılmış listelerindeki odalar
 *  - Supplier: yalnız kazandığı mesajlaşılmış listelerin odaları
 *  - Admin: tüm odalar
 */
export function useRooms() {
  return useQuery({
    queryKey: messageKeys.rooms,
    queryFn: async () => {
      const res = await api<{ ok: true; data: { items: ChatRoom[] } }>(`/messages/rooms`)
      return res.data.items
    },
    refetchInterval: 30_000, // 30s polling
  })
}

/**
 * SSE subscription — EventSource ile backend stream'e bağlanır,
 * yeni mesaj geldiğinde ilgili query cache'lerini invalidate eder.
 *
 * EventSource'un auto-reconnect özelliği vardır; bağlantı kopunca
 * `error` event'i fırlatır, browser otomatik yeniden bağlanmaya çalışır.
 * Manual reconnect için `readyState === CLOSED` durumunda yeniden bağlanırız.
 */
export function useMessageStream(listId: string | undefined) {
  const qc = useQueryClient()
  const eventSourceRef = useRef<EventSource | null>(null)

  useEffect(() => {
    if (!listId) return

    let cancelled = false

    const connect = () => {
      if (cancelled) return
      const url = `/api/purchase-lists/${listId}/messages/stream`
      const es = new EventSource(url, { withCredentials: true })
      eventSourceRef.current = es

      es.addEventListener('message', (e: MessageEvent<string>) => {
        try {
          const message = JSON.parse(e.data) as MessageRow
          // Cache'i güncelle: mesajı ekle veya no-op
          qc.setQueryData<InfiniteData<MessagePage, string | null>>(
            messageKeys.byList(listId),
            (previous) => {
              if (!previous) {
                return {
                  pages: [{ items: [message], nextCursor: null }],
                  pageParams: [null],
                }
              }
              if (
                previous.pages.some((page) => page.items.some((item) => item.id === message.id))
              ) {
                return previous
              }
              const [firstPage, ...otherPages] = previous.pages
              if (!firstPage) return previous
              const items = [...firstPage.items, message].sort((left, right) => {
                const timeDifference = Date.parse(left.createdAt) - Date.parse(right.createdAt)
                return timeDifference || left.id.localeCompare(right.id)
              })
              return {
                ...previous,
                pages: [{ ...firstPage, items }, ...otherPages],
              }
            },
          )
          // rooms unread count da güncellenmeli (sidebar badge)
          qc.invalidateQueries({ queryKey: messageKeys.rooms })
        } catch {
          // parse error — yoksay
        }
      })

      es.addEventListener('connected', () => {
        // History fetch ile stream kurulumu arasındaki dar yarış penceresini kapatır.
        qc.invalidateQueries({ queryKey: messageKeys.byList(listId) })
      })

      es.onerror = () => {
        // Browser auto-reconnect dener; readyState === CLOSED ise
        // manuel yeniden bağlanmamız gerek
        if (es.readyState === EventSource.CLOSED) {
          es.close()
          eventSourceRef.current = null
          if (!cancelled) {
            setTimeout(connect, SSE_RECONNECT_MS)
          }
        }
        // CLOSED değilse (CONNECTING durumu) browser zaten yeniden bağlanıyor
      }
    }

    connect()

    return () => {
      cancelled = true
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [listId, qc])
}
